import { Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Link as MuiLink,
  Theme,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React from "react";
import { Link } from "react-router-dom";

import { monospaceStyle, panelShadow } from "../../../theme";
import {
  Ecosystem,
  EcosystemEdge,
  EcosystemEdgeType,
  EcosystemLane,
  EcosystemNode,
} from "../../../types/analytics";
import { EntityClickHandler, getEntityTypeEntry } from "../../../types/entityRegistry";
import { EntityRef, ProductCard } from "../../../types/productPlane";
import {
  UNNAMED_ORGANIZATION,
  formatCode,
  productLevelAndStatus,
} from "../../Explore/productFields";
import {
  AnalyticsSelection,
  exploreAddress,
  selectionOf,
} from "../analyticsState";
import {
  LineSample,
  NODE_TYPE_LABELS,
  counted,
  edgeTitle,
  nodeName,
  structureReading,
} from "./EcosystemParts";

// The selection that opens a node's products in Explore, where one filter
// reproduces them: a kind by its category, a maker by its maker pick, a
// programme by its programme pick. The pick replaces the scope's own picks
// of that filter. A product has no products to list (it is opened), an
// organization that is not a maker has no filter, and the products of a
// node outside the selection are not in its scope; all are null.
export const nodeSelection = (
  selection: AnalyticsSelection,
  node: EcosystemNode,
): AnalyticsSelection | null => {
  const base = selectionOf(selection);
  if (node.outside) {
    return null;
  }
  switch (node.type) {
    case "kind":
      return { ...base, category: [node.entity.id] };
    case "organization":
      return node.roles.includes("maker") ? { ...base, maker: [node.entity.id] } : null;
    case "project":
      return { ...base, programme: [node.entity.id] };
    case "product":
      return null;
  }
};

const EDGE_GROUPS: { type: EcosystemEdgeType; label: string }[] = [
  { type: "structure", label: "Structure" },
  { type: "role", label: "Roles" },
  { type: "programme", label: "Programmes" },
  { type: "participation", label: "Programme parts" },
  { type: "kind", label: "Kind" },
];

interface Partner {
  key: string;
  node: EcosystemNode;
  edge: EcosystemEdge;
}

// The edges of a node by type, each with the node at its other end
export const partnersOf = (
  eco: Ecosystem,
  id: string,
): Record<EcosystemEdgeType, Partner[]> => {
  const byId = new Map(eco.nodes.map((node) => [node.id, node]));
  const grouped: Record<EcosystemEdgeType, Partner[]> = {
    structure: [],
    role: [],
    programme: [],
    participation: [],
    kind: [],
  };
  eco.edges.forEach((edge, index) => {
    const otherId =
      edge.source === id ? edge.target : edge.target === id ? edge.source : null;
    const other = otherId ? byId.get(otherId) : undefined;
    if (other) {
      grouped[edge.type].push({ key: `${index}`, node: other, edge });
    }
  });
  return grouped;
};

// What a lane says a node is, before the names of what it is reached
// through: "maker of NG-410"
const LANE_WORDS: Record<EcosystemLane, string> = {
  own: "own product",
  composition: "part, payload or host",
  variant: "variant",
  linked: "linked product",
  maker: "maker",
  user: "operator or buyer",
  programme: "programme",
  portfolio: "other product",
};

// The lanes a link between two products puts a node in: it says itself
const STRUCTURE_LANES: ReadonlySet<EcosystemLane> = new Set<EcosystemLane>([
  "composition",
  "variant",
  "linked",
]);

// Names in a list: "A", "A and B", "A, B and 3 more"
const namesOf = (names: string[]): string =>
  names.length <= 2
    ? names.join(" and ")
    : `${names.slice(0, 2).join(", ")} and ${names.length - 2} more`;

// Why a node is on its ring, from its lane and the nodes a ring nearer the
// centre it is linked to: a link between two products in its own sentence
// ("Brisling is a variant of Skerry"), else what the lane makes it and of
// what ("maker of NG-410 and Skerry"). Null for the centre. A part in a
// programme sets no ring, and is no reason.
export const laneReading = (eco: Ecosystem, node: EcosystemNode): string | null => {
  if (node.ring === 0 || node.lane === null) {
    return null;
  }
  const byId = new Map(eco.nodes.map((entry) => [entry.id, entry]));
  const through: { node: EcosystemNode; edge: EcosystemEdge }[] = [];
  for (const edge of eco.edges.filter((entry) => entry.type !== "participation")) {
    const otherId =
      edge.source === node.id ? edge.target : edge.target === node.id ? edge.source : null;
    const other = otherId ? byId.get(otherId) : undefined;
    if (other && other.ring === node.ring - 1 && !through.some((t) => t.node.id === other.id)) {
      through.push({ node: other, edge });
    }
  }
  const link = through.find(({ edge }) => edge.type === "structure");
  if (link && STRUCTURE_LANES.has(node.lane)) {
    const name = (id: string) => {
      const found = byId.get(id);
      return found ? nodeName(found) : id;
    };
    return structureReading(link.edge, name(link.edge.source), name(link.edge.target));
  }
  if (through.length === 0) {
    return LANE_WORDS[node.lane];
  }
  const names = namesOf(through.map((entry) => nodeName(entry.node)));
  return node.lane === "linked" ? `linked to ${names}` : `${LANE_WORDS[node.lane]} of ${names}`;
};

// A small uppercase head, as the fields of the designs carry
const HEAD_SX = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "text.secondary",
} as const;

// An action that reads as a link
const ACTION_SX = {
  p: 0,
  minWidth: 0,
  textTransform: "none",
  fontSize: "12.5px",
  fontWeight: 400,
  lineHeight: 1.5,
  justifyContent: "flex-start",
  textAlign: "left",
  "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
} as const;

// A monospace caption, as the panel's counts are written
const CAPTION_SX = { ...monospaceStyle, fontSize: "11px", lineHeight: 1.4 } as const;

// The name of an entity a section lists: it opens the entity's detail
// where the detail views know its type, else it is plain text
const EntityName: React.FC<{ entity: EntityRef; onOpenEntity: EntityClickHandler }> = ({
  entity,
  onOpenEntity,
}) => {
  const name = entity.name ?? (entity.type === "organization" ? UNNAMED_ORGANIZATION : entity.id);
  return getEntityTypeEntry(entity.type) ? (
    <Button size="small" onClick={() => onOpenEntity(entity.id, entity.type)} sx={ACTION_SX}>
      {name}
    </Button>
  ) : (
    <Box component="span" sx={{ lineHeight: 1.5 }}>
      {name}
    </Box>
  );
};

// A section of facts: a small head, then its entries in a list, then how
// many more there are
const FactList: React.FC<{
  label: string;
  more: number;
  children: React.ReactNode;
}> = ({ label, more, children }) => (
  <Box component="section" aria-label={label} sx={{ mt: 0.5 }}>
    <Box sx={HEAD_SX}>{label}</Box>
    <Box
      component="ul"
      sx={{ m: 0, mt: 0.25, p: 0, listStyle: "none", display: "grid", gap: 0.25 }}
    >
      {children}
      {more > 0 && (
        <Box component="li" sx={{ ...CAPTION_SX, color: "text.secondary" }}>
          +{more} more
        </Box>
      )}
    </Box>
  </Box>
);

// What a product's card says, as Explore's result card says it: its
// category (or the category its documents state, quoted), its level and
// status, its makers with their roles, its capabilities and its sources
const ProductFacts: React.FC<{ card: ProductCard; onOpenEntity: EntityClickHandler }> = ({
  card,
  onOpenEntity,
}) => {
  const levelAndStatus = productLevelAndStatus(card);
  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", columnGap: 1 }}>
        {card.category ? (
          <EntityName entity={card.category} onOpenEntity={onOpenEntity} />
        ) : card.category_stated ? (
          <Box component="span" title="As its documents state it" sx={{ color: "text.secondary" }}>
            &ldquo;{card.category_stated}&rdquo;
          </Box>
        ) : null}
        {levelAndStatus && (
          <Typography component="span" color="text.secondary" sx={CAPTION_SX}>
            {levelAndStatus}
          </Typography>
        )}
      </Box>
      {(card.makers.length > 0 || card.more_makers > 0) && (
        <FactList label="Makers" more={card.more_makers}>
          {card.makers.map((maker) => (
            <Box
              component="li"
              key={maker.organization.id}
              sx={{ display: "flex", alignItems: "baseline", gap: 1 }}
            >
              <EntityName entity={maker.organization} onOpenEntity={onOpenEntity} />
              <Typography component="span" color="text.disabled" sx={CAPTION_SX}>
                {maker.roles.map(formatCode).join(", ")}
              </Typography>
            </Box>
          ))}
        </FactList>
      )}
      {(card.capabilities.length > 0 || card.more_capabilities > 0) && (
        <FactList label="Capabilities" more={card.more_capabilities}>
          {card.capabilities.map((capability) => (
            <li key={capability.id}>
              <EntityName entity={capability} onOpenEntity={onOpenEntity} />
            </li>
          ))}
        </FactList>
      )}
      <Typography variant="caption" component="div" color="text.secondary" sx={monospaceStyle}>
        {counted(card.source_count, "source")}
        {card.latest_source ? ` · latest ${card.latest_source}` : ""}
      </Typography>
    </>
  );
};

interface EcosystemPanelProps {
  eco: Ecosystem;
  node: EcosystemNode;
  selection: AnalyticsSelection;
  onSelect: (id: string | null) => void;
  onRecentre: (node: EcosystemNode) => void;
  onOpenEntity: EntityClickHandler;
}

// The panel of a selected node, raised over the top right of the canvas:
// its name, which opens its detail; what it is, where it sits and why; what
// a product's card says, or the kinds an organization or a programme has
// products of; the products it stands for in Explore and the documents
// behind it; its edges and the way on
const EcosystemPanel: React.FC<EcosystemPanelProps> = ({
  eco,
  node,
  selection,
  onSelect,
  onRecentre,
  onOpenEntity,
}) => {
  const partners = partnersOf(eco, node.id);
  const picked = nodeSelection(selection, node);
  const count = counted(node.product_count, "product");
  const links = EDGE_GROUPS.reduce((sum, { type }) => sum + partners[type].length, 0);
  const centre = node.ring === 0;
  const why = laneReading(eco, node);
  // A product is one product: its count says nothing
  const product = node.type === "product";
  return (
    <Box
      component="aside"
      aria-label="Selected node"
      sx={{
        position: "absolute",
        top: 14,
        right: 14,
        zIndex: 5,
        width: 310,
        maxWidth: "calc(100% - 28px)",
        maxHeight: "calc(100% - 28px)",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        p: "14px",
        overflow: "auto",
        fontSize: "12.5px",
        border: 1,
        borderColor: "divider",
        borderRadius: (theme) => `${theme.radius.surface}px`,
        backgroundColor: "background.raised",
        boxShadow: (theme) => panelShadow(theme),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Typography
          component="h3"
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.45,
            overflowWrap: "anywhere",
          }}
        >
          {/* The name opens the node's detail, as the other views' names do */}
          <Button
            size="small"
            title={`Open the ${NODE_TYPE_LABELS[node.type]}`}
            onClick={() => onOpenEntity(node.entity.id, node.entity.type)}
            sx={{
              ...ACTION_SX,
              fontSize: "inherit",
              fontWeight: "inherit",
              lineHeight: "inherit",
              color: "text.primary",
              display: "inline",
              verticalAlign: "baseline",
            }}
          >
            {nodeName(node)}
          </Button>
        </Typography>
        {centre && (
          <Chip
            label="centre"
            size="small"
            sx={(theme: Theme) => ({
              height: 20,
              fontSize: "11.5px",
              borderColor: alpha(theme.palette.accent.main, 0.45),
              // The text of an accent chip, as the value chain's cards write it
              color: theme.palette.chart.accentText,
              backgroundColor: alpha(theme.palette.accent.main, 0.1),
            })}
          />
        )}
        <IconButton
          size="small"
          aria-label="Clear selection"
          onClick={() => onSelect(null)}
          sx={{ m: "-4px" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Typography variant="caption" component="div" sx={monospaceStyle}>
        {NODE_TYPE_LABELS[node.type]} · {centre ? "centre" : `ring ${node.ring}`}
      </Typography>

      {why && (
        <Typography
          variant="caption"
          component="div"
          color="text.secondary"
          data-testid="lane-reading"
          sx={{ lineHeight: 1.45 }}
        >
          {why}
        </Typography>
      )}

      {node.type === "organization" && node.roles.length > 0 && (
        <Typography variant="caption" component="div" color="text.secondary" sx={monospaceStyle}>
          {node.roles.join(", ")}
          {node.country ? ` · ${node.country}` : ""}
        </Typography>
      )}

      {node.card && <ProductFacts card={node.card} onOpenEntity={onOpenEntity} />}

      {(node.kinds.length > 0 || node.more_kinds > 0) && (
        <FactList label="Kinds of its products" more={node.more_kinds}>
          {node.kinds.map((kind) => (
            <li key={kind.id}>
              <EntityName entity={kind} onOpenEntity={onOpenEntity} />
            </li>
          ))}
        </FactList>
      )}

      <Typography variant="caption" component="div" color="text.secondary" sx={monospaceStyle}>
        {!product &&
          (picked ? (
            <MuiLink component={Link} to={exploreAddress(picked)} underline="hover">
              {count}
            </MuiLink>
          ) : (
            <span>{count}</span>
          ))}
        {!product && " · "}
        {`${counted(node.articles, "article")} · ${counted(links, "edge")} drawn`}
      </Typography>

      {node.outside && (
        <Typography variant="caption" component="div" color="text.secondary">
          {node.type === "organization"
            ? "Outside the selection: only products outside it are behind it."
            : "Outside the selection: linked in by a stated link to a product in it."}
        </Typography>
      )}

      {EDGE_GROUPS.filter(({ type }) => partners[type].length > 0).map(
        ({ type, label }) => (
          <Box
            component="section"
            key={type}
            aria-label={`${label} of the node`}
            sx={{ mt: 0.5 }}
          >
            <Box sx={{ ...HEAD_SX, display: "flex", alignItems: "center", gap: 0.75 }}>
              <LineSample type={type} />
              {label}
            </Box>
            <Box
              component="ul"
              sx={{ m: 0, mt: 0.5, p: 0, listStyle: "none", display: "grid", gap: 0.5 }}
            >
              {partners[type].map(({ key, node: partner, edge }) => (
                <Box component="li" key={key}>
                  <Button size="small" onClick={() => onSelect(partner.id)} sx={ACTION_SX}>
                    {nodeName(partner)}
                  </Button>
                  <Typography
                    variant="caption"
                    component="div"
                    color="text.disabled"
                    sx={{ ...monospaceStyle, fontSize: "11px", lineHeight: 1.4 }}
                  >
                    {edgeTitle(edge, edge.source === partner.id)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ),
      )}

      <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap", mt: 0.5 }}>
        <Button
          size="small"
          disabled={centre}
          onClick={() => onRecentre(node)}
          sx={ACTION_SX}
        >
          Re-centre
        </Button>
      </Box>
    </Box>
  );
};

export default EcosystemPanel;
